import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "./ui/button";
import { ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Partner Logo Component - Defined outside to avoid re-creation on render
const PartnerLogo = ({ partner, size = "md" }) => (
  <a 
    href={partner.website_url || "#"}
    target={partner.website_url ? "_blank" : "_self"}
    rel="noopener noreferrer"
    className={`flex-shrink-0 bg-white rounded-2xl p-4 flex items-center justify-center hover:shadow-lg transition-all hover:scale-105 ${
      size === "lg" ? "min-w-[200px] h-[120px]" : 
      size === "md" ? "min-w-[160px] h-[100px]" : "min-w-[120px] h-[80px]"
    }`}
  >
    <img 
      src={partner.logo_url}
      alt={partner.name}
      className="max-h-full max-w-full object-contain"
    />
  </a>
);

const SponsorsSection = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await axios.get(`${API}/partners`);
        setPartners(response.data.partners || []);
      } catch (error) {
        console.error("Error fetching partners:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPartners();
  }, []);

  // Group partners by category
  const titleSponsor = partners.filter(p => p.category === "title_sponsor");
  const goldSponsors = partners.filter(p => p.category === "gold_sponsor");
  const silverSponsors = partners.filter(p => p.category === "silver_sponsor");
  const regularPartners = partners.filter(p => p.category === "partner");
  const govPartners = partners.filter(p => p.category === "government");
  const mediaPartners = partners.filter(p => p.category === "media");

  // Real sponsors from past editions (Hyderabad 2024 & Bengaluru 2025)
  const pastEditionSponsors = [
    { name: "IRIE", type: "Title Partner", logo: "https://customer-assets.emergentagent.com/job_chennai-finrun/artifacts/97tk5mk3_DR6A3753.JPG" },
    { name: "Prosperti", type: "Gold Partner" },
    { name: "Govt. of Telangana", type: "Government Partner" },
    { name: "Dept. of Cyber Crimes", type: "Government Partner" },
    { name: "SBI Life", type: "Insurance Partner" },
    { name: "Decathlon", type: "Sports Partner" },
    { name: "AIG Hospitals", type: "Health Partner" },
    { name: "Aquatron", type: "Hydration Partner" },
    { name: "Zycle", type: "Fitness Partner" },
    { name: "Sid's Farm", type: "Nutrition Partner" },
    { name: "T-Hub", type: "Innovation Partner" },
    { name: "ITEA", type: "Technology Partner" }
  ];

  // Government and institutional supporters
  const govSupporters = [
    "Govt. of Telangana",
    "Dept. of Cyber Crimes", 
    "T-Hub",
    "ITEA"
  ];

  const pastSponsors = [
    "SBI Life",
    "Prosperti",
    "Decathlon",
    "AIG Hospitals",
    "Aquatron",
    "Zycle",
    "Sid's Farm"
  ];

  return (
    <section className="section-spacing bg-[#FAFAFA]" data-testid="sponsors-section">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-[#007AFF] text-sm uppercase tracking-[0.2em] font-bold mb-4">
            Our Partners
          </p>
          <h2 className="text-4xl sm:text-5xl font-bold text-[#0A0A0A] font-['Outfit'] mb-4">
            Supported By Industry Leaders
          </h2>
          <p className="text-[#4B5563] text-lg max-w-2xl mx-auto">
            Join the league of organizations making a difference in the fight against cybercrime.
          </p>
        </div>

        {/* Title Sponsor - If exists */}
        {titleSponsor.length > 0 && (
          <div className="mb-12 text-center">
            <p className="text-sm uppercase tracking-wider text-[#FFD700] font-bold mb-4">Title Sponsor</p>
            <div className="flex justify-center">
              {titleSponsor.map(partner => (
                <a 
                  key={partner.id}
                  href={partner.website_url || "#"}
                  target={partner.website_url ? "_blank" : "_self"}
                  rel="noopener noreferrer"
                  className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all"
                >
                  <img 
                    src={partner.logo_url}
                    alt={partner.name}
                    className="h-24 md:h-32 w-auto object-contain"
                  />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Gold Sponsors - If exists */}
        {goldSponsors.length > 0 && (
          <div className="mb-12">
            <p className="text-sm uppercase tracking-wider text-[#FFD700] font-bold mb-6 text-center">Gold Sponsors</p>
            <div className="flex flex-wrap justify-center gap-6">
              {goldSponsors.map(partner => (
                <PartnerLogo key={partner.id} partner={partner} size="lg" />
              ))}
            </div>
          </div>
        )}

        {/* Silver Sponsors - If exists */}
        {silverSponsors.length > 0 && (
          <div className="mb-12">
            <p className="text-sm uppercase tracking-wider text-gray-500 font-bold mb-6 text-center">Silver Sponsors</p>
            <div className="flex flex-wrap justify-center gap-4">
              {silverSponsors.map(partner => (
                <PartnerLogo key={partner.id} partner={partner} size="md" />
              ))}
            </div>
          </div>
        )}

        {/* Regular Partners - Either from DB or fallback */}
        <div className="relative overflow-hidden mb-16">
          {partners.length > 0 ? (
            /* Show real partners if available */
            <div className="flex flex-wrap justify-center gap-4">
              {regularPartners.map(partner => (
                <PartnerLogo key={partner.id} partner={partner} size="md" />
              ))}
            </div>
          ) : (
            /* Show past edition sponsors */
            <div className="flex animate-marquee">
              {[...pastEditionSponsors, ...pastEditionSponsors].map((sponsor, i) => (
                <div 
                  key={`${sponsor.name}-${i}`}
                  className="flex-shrink-0 mx-4 bg-white rounded-2xl p-6 min-w-[200px] text-center shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-[#FFD700]/20 to-[#FF3B30]/20 rounded-xl mx-auto mb-3 flex items-center justify-center">
                    <span className="text-2xl font-bold text-[#0A0A0A]">
                      {sponsor.name.charAt(0)}
                    </span>
                  </div>
                  <p className="font-semibold text-[#0A0A0A]">{sponsor.name}</p>
                  <p className="text-xs text-[#9CA3AF]">{sponsor.type}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Government & Institutional Support */}
        <div className="bg-[#111827] rounded-3xl p-8 md:p-12 mb-16">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-[#FFD700] text-sm uppercase tracking-[0.2em] font-bold mb-4">
                Institutional Support
              </p>
              <h3 className="text-3xl font-bold text-white font-['Outfit'] mb-4">
                Backed By Government & Industry Bodies
              </h3>
              <p className="text-gray-400 mb-6">
                The FinRun is supported by the Government of Telangana, Department of Cyber Crimes, 
                and leading industry associations committed to cybersecurity awareness.
              </p>
              
              {/* Government Partners from DB */}
              {govPartners.length > 0 ? (
                <div className="flex flex-wrap gap-4">
                  {govPartners.map(partner => (
                    <a 
                      key={partner.id}
                      href={partner.website_url || "#"}
                      target={partner.website_url ? "_blank" : "_self"}
                      rel="noopener noreferrer"
                      className="bg-white/10 hover:bg-white/20 rounded-xl p-3 transition-colors"
                    >
                      <img 
                        src={partner.logo_url}
                        alt={partner.name}
                        className="h-10 w-auto object-contain brightness-0 invert"
                      />
                    </a>
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-4">
                  {govSupporters.map((org) => (
                    <span 
                      key={org}
                      className="bg-white/10 text-white px-4 py-2 rounded-full text-sm font-medium"
                    >
                      {org}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="text-center">
              <div className="inline-block bg-white/5 border border-white/10 rounded-2xl p-8">
                <p className="text-5xl font-black font-['Outfit'] text-[#FFD700] mb-2">
                  {partners.length > 0 ? `${partners.length}+` : "50+"}
                </p>
                <p className="text-white">Supporting Organizations</p>
              </div>
            </div>
          </div>
        </div>

        {/* Media Partners */}
        {mediaPartners.length > 0 && (
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit'] text-center mb-8">
              Media Partners
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              {mediaPartners.map(partner => (
                <PartnerLogo key={partner.id} partner={partner} size="sm" />
              ))}
            </div>
          </div>
        )}

        {/* Past Sponsors - Only show if no real partners */}
        {partners.length === 0 && (
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit'] text-center mb-8">
              Past Partners
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              {pastSponsors.map((sponsor) => (
                <div 
                  key={sponsor}
                  className="bg-white rounded-xl px-6 py-3 shadow-sm text-[#4B5563] font-medium"
                >
                  {sponsor}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="bg-gradient-to-r from-[#007AFF] to-[#0062CC] rounded-3xl p-8 md:p-12 text-center">
          <h3 className="text-3xl md:text-4xl font-bold text-white font-['Outfit'] mb-4">
            Partner With Purpose
          </h3>
          <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
            Join The FinRun as a sponsor and be part of India's largest movement combining 
            fitness with cybersecurity awareness. Multiple sponsorship tiers available.
          </p>
          <Link to="/sponsor">
            <Button 
              className="bg-white text-[#007AFF] hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-semibold"
              data-testid="sponsors-cta-btn"
            >
              Become a Sponsor
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SponsorsSection;
